import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Activity } from 'lucide-react'
import { publishingApiClient } from '../../api/http/publishingApiClient'
import type { WireArticle } from '../../api/types'
import type { WorkflowHistoryEntry } from '../metrics/types'

interface RecentActivityTrendWidgetProps {
  articles: WireArticle[]
  embedded?: boolean
}

type TrendRow = {
  day: string
  count: number
}

type ChartColors = {
  fg: string
  fgSecondary: string
  line: string
  tooltipBg: string
  tooltipFg: string
}

type ActivityRow = Omit<WorkflowHistoryEntry, 'changedAt'> & {
  changedAt: string
  articleId: string
}

function resolveChartColors(): ChartColors {
  if (typeof window === 'undefined') {
    return {
      fg: '#111111',
      fgSecondary: '#6b7280',
      line: '#e5e7eb',
      tooltipBg: '#111111',
      tooltipFg: '#fcfcfc',
    }
  }
  const s = getComputedStyle(document.documentElement)
  const get = (v: string) => s.getPropertyValue(v).trim()
  return {
    fg: get('--gray-12') || '#111111',
    fgSecondary: get('--gray-11') || '#6b7280',
    line: get('--gray-6') || '#e5e7eb',
    tooltipBg: get('--gray-12') || '#111111',
    tooltipFg: get('--gray-1') || '#fcfcfc',
  }
}

function useChartTheme(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(resolveChartColors)
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(resolveChartColors()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  return colors
}

function normalizeTimestamp(value: unknown): string | null {
  if (typeof value === 'string') {
    const ms = new Date(value).getTime()
    return Number.isFinite(ms) ? new Date(ms).toISOString() : null
  }
  if (typeof value === 'number') {
    const ms = value > 10_000_000_000 ? value : value * 1000
    const parsed = new Date(ms).getTime()
    return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null
  }
  if (value instanceof Date) {
    const ms = value.getTime()
    return Number.isFinite(ms) ? new Date(ms).toISOString() : null
  }
  return null
}

function dayLabel(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${month}/${day}`
}

function buildDayRange(startDay: string, endDay: string): string[] {
  const start = new Date(`${startDay}T00:00:00.000Z`)
  const end = new Date(`${endDay}T00:00:00.000Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return []

  const out: string[] = []
  const cursor = new Date(start)
  while (cursor <= end) {
    out.push(cursor.toISOString().split('T')[0])
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return out
}

function useRecentActivityTrend(articles: WireArticle[]) {
  const [rows, setRows] = useState<ActivityRow[]>([])
  const [loading, setLoading] = useState(false)

  const stableKey = useMemo(() => articles.slice(0, 12).map((article) => article.id).join(','), [articles])

  useEffect(() => {
    const ids = stableKey ? stableKey.split(',').filter(Boolean) : []
    if (ids.length === 0) {
      setRows([])
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    void Promise.allSettled(
      ids.map(async (id) => {
        const { data } = await publishingApiClient.get<WorkflowHistoryEntry[]>(`/drafts/${id}/workflow-history`)
        return data.flatMap((entry) => {
          const changedAt = normalizeTimestamp((entry as { changedAt?: unknown }).changedAt)
          if (!changedAt) return []
          return [{ ...entry, changedAt, articleId: id }]
        })
      }),
    ).then((results) => {
      if (cancelled) return
      const next = results
        .filter((result): result is PromiseFulfilledResult<ActivityRow[]> => result.status === 'fulfilled')
        .flatMap((result) => result.value)
        .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
        .slice(0, 60)

      setRows(next)
      setLoading(false)
    }).catch(() => {
      if (cancelled) return
      setRows([])
      setLoading(false)
    })

    return () => { cancelled = true }
  }, [stableKey])

  return { rows, loading }
}

export function RecentActivityTrendWidget({ articles, embedded = false }: RecentActivityTrendWidgetProps) {
  const { rows, loading } = useRecentActivityTrend(articles)
  const colors = useChartTheme()

  const trend = useMemo<TrendRow[]>(() => {
    const map = new Map<string, number>()
    for (const row of rows) {
      const day = row.changedAt.split('T')[0]
      map.set(day, (map.get(day) ?? 0) + 1)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([day, count]) => ({ day, count }))
  }, [rows])

  const chartSeries = useMemo(() => {
    if (trend.length === 0) {
      return {
        days: [] as string[],
        dailyCounts: [] as number[],
        average: 0,
      }
    }

    const firstDay = trend[0].day
    const lastDay = trend[trend.length - 1].day
    const allDays = buildDayRange(firstDay, lastDay)
    const byDay = new Map(trend.map((row) => [row.day, row.count]))
    const dailyCounts = allDays.map((day) => byDay.get(day) ?? 0)
    const total = dailyCounts.reduce((sum, count) => sum + count, 0)
    const average = dailyCounts.length > 0 ? total / dailyCounts.length : 0

    return {
      days: allDays,
      dailyCounts,
      average,
    }
  }, [trend])

  const chartOption = useMemo(() => {
    if (chartSeries.days.length === 0) return null

    const avgLabel = `${chartSeries.average.toFixed(1)}`
    return {
      grid: { top: 12, right: 18, bottom: 72, left: 44 },
      tooltip: {
        trigger: 'axis' as const,
        backgroundColor: colors.tooltipBg,
        borderColor: colors.line,
        textStyle: { color: colors.tooltipFg, fontSize: 11 },
        valueFormatter: (value: number) => `${value.toFixed(1)} transitions`,
      },
      xAxis: {
        type: 'category' as const,
        data: chartSeries.days,
        axisLabel: {
          color: colors.fg,
          fontSize: 11,
          fontWeight: 600,
          margin: 12,
          formatter: (value: string) => dayLabel(value),
        },
        axisLine: { lineStyle: { color: colors.line } },
      },
      yAxis: {
        type: 'value' as const,
        minInterval: 1,
        axisLabel: { color: colors.fg, fontSize: 11, fontWeight: 600 },
        splitLine: { lineStyle: { color: colors.line } },
      },
      series: [
        {
          name: 'Daily throughput',
          type: 'line' as const,
          smooth: true,
          data: chartSeries.dailyCounts,
          showSymbol: false,
          lineStyle: { width: 2, color: '#2563eb' },
          itemStyle: { color: '#2563eb' },
          areaStyle: { color: 'rgba(37, 99, 235, 0.12)' },
        },
        {
          name: `Average (${avgLabel})`,
          type: 'line' as const,
          smooth: false,
          data: chartSeries.days.map(() => Number(chartSeries.average.toFixed(2))),
          showSymbol: false,
          lineStyle: { width: 2, type: 'dashed' as const, color: '#6b7280' },
          itemStyle: { color: '#6b7280' },
        },
      ],
      legend: {
        bottom: 6,
        textStyle: { color: colors.fg, fontSize: 11, fontWeight: 600 },
      },
    }
  }, [chartSeries, colors])

  const body = (
    <div className="space-y-2 px-4 py-4">
      {loading ? (
        <div className="space-y-2 rounded-xl bg-canvas px-4 py-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-4 animate-pulse rounded bg-fill" />)}
        </div>
      ) : chartOption ? (
        <div className="rounded-xl bg-canvas px-2 py-2">
          <ReactECharts option={chartOption} style={{ height: 260 }} />
        </div>
      ) : (
        <div className="rounded-xl bg-canvas px-4 py-6 text-center text-[11px] text-fg-secondary">
          No recent workflow history yet.
        </div>
      )}

      <div className="rounded-xl bg-fill/60 px-4 py-3 text-[11px] text-fg-secondary">
        {chartSeries.days.length > 0
          ? `${rows.length} transitions across ${chartSeries.days.length} day${chartSeries.days.length !== 1 ? 's' : ''}. Avg throughput: ${chartSeries.average.toFixed(1)} per day.`
          : `${rows.length} transitions captured in the selected draft set.`}
      </div>
    </div>
  )

  if (embedded) return <div>{body}</div>

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-5 py-3">
        <Activity size={14} className="text-fg-secondary" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Recent Activity Trend</span>
      </div>
      {body}
    </section>
  )
}
